import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import Api from "../../config/Api";
import { toast } from "react-toastify";
import Table from "../Widget/Table";
import Loader from "../Widget/Loader";
import dayjs from "dayjs";
import TooltipWidget from "../Widget/TooltipWidget";
import { BsFillEyeFill } from "react-icons/bs";
import ModalBox from "../Widget/ModalBox";
import ReactToPrint from "react-to-print";
import { FaPrint } from "react-icons/fa";
import FilterComponent from "./components/FilterComponent";

import logo from "../../assets/images/logo.png";

const DEFAULT_ROWS_PER_PAGE = 10;

const PaymentHistory = () => {
  const [application, setApplication] = useState([]);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isdetailModalShow, setIsdetailModalShow] = useState(false);
  const [detailModalData, setDetailModalData] = useState(null);
  const [pagination, setPagination] = useState({
    total: 0,
    current_page: 1,
    per_page: DEFAULT_ROWS_PER_PAGE,
    last_page: 1,
  });

  const componentRef = useRef();

  // 🔍 Filters state (controlled from parent)
  const [filters, setFilters] = useState({
    district: "",
    applicationId: "",
    sortOrder: "",
  });

  const handleFilters = (updatedFilters) => {
    setFilters(updatedFilters);
  };

  const detailModalShow = (row) => {
    // console.log("DETAIL ROW ===> ", row);
    setDetailModalData(row);
    setIsdetailModalShow(true);
  };

  const detailModalClose = () => {
    setDetailModalData(null);
    setIsdetailModalShow(false);
  };

  const getDownloadedList = useCallback(async (pageNum, rowsPerPage, activeFilters) => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(pageNum),
        per_page: String(rowsPerPage),
      });

      if (activeFilters.district) {
        params.append("district_id", activeFilters.district);
      }

      if (activeFilters.applicationId) {
        params.append("search", activeFilters.applicationId);
      }

      const res = await Api(`payment_history?${params.toString()}`);
      // console.log("payment_history ===> ", res);

      if (res?.data?.status === 1) {
        window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
        setApplication(res?.data?.data || []);
        setPagination(
          res?.data?.pagination || {
            total: 0,
            current_page: pageNum,
            per_page: rowsPerPage,
            last_page: 1,
          }
        );
        setIsDataLoaded(true);
      } else {
        toast.error(res?.data?.message || "Failed to load payment history", {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
        setIsDataLoaded(true);
      }
    } catch (error) {
      console.error("Error fetching payment history:", error);
      toast.error("Something went wrong while fetching payment history", {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
      setIsDataLoaded(true);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    getDownloadedList(1, pagination.per_page, filters);
  }, [filters, getDownloadedList, pagination.per_page]);

  // 📋 Columns for table
  const columns = [
    {
      name: "#",
      selector: (row, index) =>
        (pagination.current_page - 1) * pagination.per_page + index + 1,
      sortable: true,
      width: "70px",
    },
    {
      name: "Application ID",
      selector: (row) =>
        row.application_no || row.application_id || "-", // ✅ handle both keys
      sortable: true,
      wrap: true,
    },
    {
      name: "Payment Type",
      selector: (row) =>
        !!row.application?.payment_mode ? row.application?.payment_mode : "-",
      sortable: true,
      wrap: true,
    },
    {
      name: "Payment Type Other",
      selector: (row) =>
        !!row.application?.payment_type_other
          ? row.application?.payment_type_other
          : "-",
      sortable: true,
      wrap: true,
    },
    {
      name: "Amount",
      selector: (row) => row.amount,
      sortable: true,
      wrap: true,
      sortFunction: (a, b) => Number(a?.amount || 0) - Number(b?.amount || 0),
    },
    {
      name: "Reference ID",
      selector: (row) => row.reference_id || "-",
      sortable: true,
      wrap: true,
    },
    {
      name: "Payout ID",
      selector: (row) => row.payout_id || "-",
      sortable: true,
      wrap: true,
      minWidth: "170px",
    },
    {
      name: "Status",
      selector: (row) => row.status || "-",
      sortable: true,
      wrap: true,
    },
    {
      name: "Date",
      selector: (row) =>
        row.created_at
          ? dayjs(row.created_at).format("DD MMM YYYY hh:mm A")
          : "-",
      sortable: true,
      wrap: true,
      minWidth: "170px",
    },
    {
      name: "Action",
      // ⚠️ Keep using "selector" here because your custom Table was using it earlier
      selector: (row) => (
        <div>
          <TooltipWidget title="View">
            <button
              className="btn btn-warning btn-sm"
              type="button"
              onClick={() => detailModalShow(row)}
            >
              <BsFillEyeFill />
            </button>
          </TooltipWidget>
        </div>
      ),
      sortable: false,
      wrap: true,
      width: "70px",
    },
  ];

  // 🔍 Apply filters + sorting
  const filteredData = useMemo(() => {
    let data = [...application];

    // Amount sorting is still handled on the current page.
    if (filters.sortOrder === "asc") {
      data.sort((a, b) => Number(a.amount || 0) - Number(b.amount || 0));
    } else if (filters.sortOrder === "desc") {
      data.sort((a, b) => Number(b.amount || 0) - Number(a.amount || 0));
    }

    return data;
  }, [application, filters.sortOrder]);

  const handlePageChange = (page) => {
    getDownloadedList(page, pagination.per_page, filters);
  };

  const handleRowsPerPageChange = (newRowsPerPage) => {
    getDownloadedList(1, newRowsPerPage, filters);
  };

  // Helper: formatted Application ID for print/modal
  const getApplicationId = (row) =>
    row?.application_no || row?.application_id || "-";

  return (
    <>
      <div className="bg-white p-3 rounded shadow">
        <div className="d-md-flex justify-content-between mb-3">
          <div className="flex-grow-1">
            <h5 className="text-dark">Payment History</h5>
          </div>

          <div>
            {/* 🔗 Controlled filters */}
            <FilterComponent filters={filters} onChange={handleFilters} />
          </div>
        </div>

        <div className="mt-3">
          {isDataLoaded ? (
            <Table
              columns={columns}
              data={filteredData}
              paginationServer
              paginationTotalRows={pagination.total}
              paginationPerPage={pagination.per_page}
              paginationDefaultPage={pagination.current_page}
              onChangePage={handlePageChange}
              onChangeRowsPerPage={handleRowsPerPageChange}
              progressPending={isLoading}
            />
          ) : (
            <Loader />
          )}
        </div>
      </div>

      {/* 🖨 Hidden print section */}
      <div
        ref={componentRef}
        style={{ padding: "20px", margin: "40px 10px", display: "none" }}
        className="print-section"
      >
        {detailModalData && (
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th
                  colSpan={2}
                  style={{ textAlign: "center", padding: "10px" }}
                >
                  <img
                    src={logo}
                    style={{ height: "130px", marginBottom: "30px" }}
                    alt="logo"
                  />
                </th>
              </tr>
              <tr>
                <th
                  colSpan={2}
                  style={{
                    textAlign: "center",
                    padding: "10px",
                    color: "#252372",
                    fontSize: "25px",
                    fontWeight: "500",
                  }}
                >
                  Payment Receipt
                </th>
              </tr>
            </thead>

            <tbody>
              <tr>
                <td
                  style={{
                    padding: "10px 10px 0px 10px",
                    verticalAlign: "top",
                  }}
                  colSpan={2}
                >
                  <h5
                    style={{
                      margin: 0,
                      color: "#000000de",
                      fontWeight: "500",
                    }}
                  >
                    Application ID: {getApplicationId(detailModalData)}
                  </h5>
                </td>
              </tr>
              <tr>
                <td
                  style={{ padding: "10px", verticalAlign: "top" }}
                >
                  <p
                    style={{
                      margin: "2px 0",
                      color: "#000000de",
                      fontSize: "18px",
                    }}
                  >
                    {detailModalData?.account_holder_name}
                  </p>
                  <p style={{ margin: "2px 0", color: "#000000de" }}>
                    {detailModalData?.email}
                  </p>
                </td>
                <td
                  style={{
                    padding: "10px",
                    textAlign: "end",
                    verticalAlign: "top",
                  }}
                >
                  <p style={{ margin: "2px 0", color: "#000000de" }}>
                    {detailModalData?.created_at
                      ? dayjs(detailModalData?.created_at).format(
                          "DD MMM YYYY hh:mm A"
                        )
                      : "-"}
                  </p>
                  <p style={{ margin: "2px 0", color: "#000000de" }}>
                    {detailModalData?.contact}
                  </p>
                </td>
              </tr>

              <tr>
                <td style={{ padding: "5px 10px" }}>
                  <div
                    style={{
                      display: "flex",
                      verticalAlign: "center",
                      gap: "5px",
                    }}
                  >
                    <label
                      className="small fw-bold mb-1"
                      style={{ color: "#000000de" }}
                    >
                      Account Number:
                    </label>
                    <p style={{ color: "#000000de" }}>
                      {detailModalData?.account_no}
                    </p>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      verticalAlign: "center",
                      gap: "5px",
                    }}
                  >
                    <label
                      className="small fw-bold mb-1"
                      style={{ color: "#000000de" }}
                    >
                      IFSC:
                    </label>
                    <p style={{ color: "#000000de" }}>
                      {detailModalData?.ifsc}
                    </p>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      verticalAlign: "center",
                      gap: "5px",
                    }}
                  >
                    <label
                      className="small fw-bold mb-1"
                      style={{ color: "#000000de" }}
                    >
                      Payment Type:
                    </label>
                    <p style={{ color: "#000000de" }}>
                      {!!detailModalData?.application?.payment_mode
                        ? detailModalData?.application?.payment_mode
                        : "-"}
                    </p>
                  </div>
                </td>
                <td style={{ textAlign: "end", padding: "5px 10px" }}>
                  <h3 style={{ color: "#252372" }}>
                    ₹{detailModalData?.amount}
                  </h3>
                  <h6
                    style={{
                      color: "#387123",
                      fontSize: "18px",
                    }}
                  >
                    {detailModalData?.status}
                  </h6>
                </td>
              </tr>
              <tr style={{ height: "30px" }}></tr>

              <tr>
                <td
                  style={{
                    border: "1px solid #d8d8ff",
                    padding: "5px 10px",
                  }}
                >
                  <label
                    className="small fw-bold mb-1"
                    style={{ color: "#000000de" }}
                  >
                    Reference ID
                  </label>
                </td>
                <td
                  style={{
                    border: "1px solid #d8d8ff",
                    padding: "5px 10px",
                  }}
                >
                  <p style={{ color: "#000000de" }}>
                    {detailModalData?.reference_id}
                  </p>
                </td>
              </tr>
              <tr>
                <td
                  style={{
                    border: "1px solid #d8d8ff",
                    padding: "5px 10px",
                  }}
                >
                  <label
                    className="small fw-bold mb-1"
                    style={{ color: "#000000de" }}
                  >
                    Payout ID
                  </label>
                </td>
                <td
                  style={{
                    border: "1px solid #d8d8ff",
                    padding: "5px 10px",
                  }}
                >
                  <p style={{ color: "#000000de" }}>
                    {detailModalData?.payout_id}
                  </p>
                </td>
              </tr>
              <tr>
                <td
                  style={{
                    border: "1px solid #d8d8ff",
                    padding: "5px 10px",
                  }}
                >
                  <label
                    className="small fw-bold mb-1"
                    style={{ color: "#000000de" }}
                  >
                    Payment Type Other
                  </label>
                </td>
                <td
                  style={{
                    border: "1px solid #d8d8ff",
                    padding: "5px 10px",
                  }}
                >
                  <p style={{ color: "#000000de" }}>
                    {!!detailModalData?.application?.payment_type_other
                      ? detailModalData?.application?.payment_type_other
                      : "-"}
                  </p>
                </td>
              </tr>
              <tr style={{ height: "40px" }}></tr>
              <tr>
                <td
                  style={{
                    border: "1px solid #d8d8ff",
                    padding: "10px",
                    textAlign: "center",
                  }}
                >
                  <h6 style={{ color: "#252372" }}>Uploaded Photo</h6>
                  {detailModalData?.application?.applicant_default_img && (
                    <img
                      src={detailModalData?.application?.applicant_default_img}
                      alt="Uploaded"
                      style={{ maxWidth: "100px", height: "auto" }}
                    />
                  )}
                </td>
                <td
                  style={{
                    border: "1px solid #d8d8ff",
                    padding: "10px",
                    textAlign: "center",
                  }}
                >
                  <h6 style={{ color: "#252372" }}>Captured Photo</h6>
                  {detailModalData?.application?.applicant_img && (
                    <img
                      src={detailModalData?.application?.applicant_img}
                      alt="Captured"
                      style={{ maxWidth: "100px", height: "auto" }}
                    />
                  )}
                </td>
              </tr>
            </tbody>
          </table>
        )}
      </div>

      {/* 🧾 Modal with details + Print button */}
      <ModalBox
        show={isdetailModalShow}
        hide={detailModalClose}
        title="Payment Detail"
        size="lg"
      >
        <ReactToPrint
          trigger={() => (
            <div className="text-end mb-2">
              <button type="button" className="btn btn-success btn-sm px-2">
                <FaPrint /> Print
              </button>
            </div>
          )}
          content={() => componentRef.current}
        />

        {detailModalData && (
          <div className="p-3">
            <div
              className="row py-2 mb-3"
              style={{ border: "1px solid #ddd" }}
            >
              <div className="col-6 col-lg-6">
                <div className="px-3 py-2 rounded text-center">
                  <h6 style={{ color: "#252372" }}>Uploaded Photo</h6>
                  {detailModalData?.application?.applicant_default_img && (
                    <img
                      src={detailModalData?.application?.applicant_default_img}
                      alt="Applicant Uploaded"
                      style={{ maxWidth: "100px", height: "auto" }}
                    />
                  )}
                </div>
              </div>
              <div className="col-6 col-lg-6">
                <div className="px-3 py-2 rounded text-center">
                  <h6 style={{ color: "#252372" }}>Captured Photo</h6>
                  {detailModalData?.application?.applicant_img && (
                    <img
                      src={detailModalData?.application?.applicant_img}
                      alt="Applicant Captured"
                      style={{ maxWidth: "100px", height: "auto" }}
                    />
                  )}
                </div>
              </div>
            </div>

            <div className="row g-3">
              <div className="col-6 col-lg-6">
                <div className="px-3 py-2 rounded paylistst">
                  <label className="small fw-bold mb-1">
                    Application ID:
                  </label>
                  <p>{getApplicationId(detailModalData)}</p>
                </div>
              </div>
              <div className="col-6 col-lg-6">
                <div className="px-3 py-2 rounded paylistst">
                  <label className="small fw-bold mb-1">Name: </label>
                  <p>{detailModalData?.account_holder_name}</p>
                </div>
              </div>
              <div className="col-6 col-lg-6">
                <div className="px-3 py-2 rounded paylistst">
                  <label className="small fw-bold mb-1">Email: </label>
                  <p>{detailModalData?.email}</p>
                </div>
              </div>
              <div className="col-6 col-lg-6">
                <div className="px-3 py-2 rounded paylistst">
                  <label className="small fw-bold mb-1">
                    Contact Number:
                  </label>
                  <p>{detailModalData?.contact}</p>
                </div>
              </div>
              <div className="col-6 col-lg-6">
                <div className="px-3 py-2 rounded paylistst">
                  <label className="small fw-bold mb-1">Amount: </label>
                  <p>₹{detailModalData?.amount}</p>
                </div>
              </div>
              <div className="col-6 col-lg-6">
                <div className="px-3 py-2 rounded paylistst">
                  <label className="small fw-bold mb-1">
                    Account Number:
                  </label>
                  <p>{detailModalData?.account_no}</p>
                </div>
              </div>
              <div className="col-6 col-lg-6">
                <div className="px-3 py-2 rounded paylistst">
                  <label className="small fw-bold mb-1">IFSC: </label>
                  <p>{detailModalData?.ifsc}</p>
                </div>
              </div>
              <div className="col-6 col-lg-6">
                <div className="px-3 py-2 rounded paylistst">
                  <label className="small fw-bold mb-1">Payment Type: </label>
                  <p>
                    {!!detailModalData?.application?.payment_mode
                      ? detailModalData?.application?.payment_mode
                      : "-"}
                  </p>
                </div>
              </div>
              <div className="col-6 col-lg-6">
                <div className="px-3 py-2 rounded paylistst">
                  <label className="small fw-bold mb-1">
                    Payment Type Other:
                  </label>
                  <p>
                    {!!detailModalData?.application?.payment_type_other
                      ? detailModalData?.application?.payment_type_other
                      : "-"}
                  </p>
                </div>
              </div>
              <div className="col-6 col-lg-6">
                <div className="px-3 py-2 rounded paylistst">
                  <label className="small fw-bold mb-1">Reference ID: </label>
                  <p>{detailModalData?.reference_id}</p>
                </div>
              </div>
              <div className="col-6 col-lg-6">
                <div className="px-3 py-2 rounded paylistst">
                  <label className="small fw-bold mb-1">Payout ID: </label>
                  <p>{detailModalData?.payout_id}</p>
                </div>
              </div>
              <div className="col-6 col-lg-6">
                <div className="px-3 py-2 rounded paylistst">
                  <label className="small fw-bold mb-1">Status: </label>
                  <p>{detailModalData?.status}</p>
                </div>
              </div>
              <div className="col-6 col-lg-6">
                <div className="px-3 py-2 rounded paylistst">
                  <label className="small fw-bold mb-1">Date: </label>
                  <p>
                    {detailModalData?.created_at
                      ? dayjs(detailModalData?.created_at).format(
                          "DD MMM YYYY hh:mm A"
                        )
                      : "-"}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </ModalBox>
    </>
  );
};

export default PaymentHistory;
